import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { func } from 'prop-types'
import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { Button } from '@gympass/yoga'

import bindActionsOnFields from '@client/utils/bindActionsOnFields'
import getAddressFromZipCode from '@client/services/address'

import useContact from '@hooks/useContact'
import useLoading from '@hooks/useLoading'
import useDynamicFields from '@hooks/useDynamicFields'
import useInternationalization from '@hooks/useInternationalization'

import DynamicForm from '@components/DynamicForm'
import LoadingButton from '@components/LoadingButton'

import useValidation from '@hooks/useFormValidation'

import * as Style from './Form.style'

export default function Form({ onSubmit, onPrevious }) {
  const { country } = useInternationalization()
  const { states } = useSelector(store => store.country)
  const { fields: contractorDataFields } = useDynamicFields('contractorData')
  const { fields: billingDataFields } = useDynamicFields('billingData')
  const { fields: financialManagerDataFields } = useDynamicFields(
    'financialManagerData'
  )
  const { fields: checkoutWarningFields } = useDynamicFields('checkoutWarning')

  const baseFields = contractorDataFields
    .concat(billingDataFields)
    .concat(financialManagerDataFields)
    .concat(checkoutWarningFields)

  const validationSchema = useValidation(baseFields)

  const { contact } = useContact()
  const { isLoading } = useLoading()

  const { register, handleSubmit, errors, setValue, control, reset } = useForm({
    defaultValues: {
      ...contact,
      city: '',
      streetAddress: '',
      state: '',
      financialOfficerName: '',
      financialOfficerEmail: '',
    },
    validationSchema,
  })

  useEffect(() => {
    reset({ ...contact })
  }, [contact])

  function handleFinancialOfficerFields(event) {
    const firstName = document.querySelector('#firstName')
    const lastName = document.querySelector('#lastName')
    const email = document.querySelector('#email')

    if (event.target.checked) {
      setValue('financialOfficerName', `${firstName.value} ${lastName.value}`)
      setValue('financialOfficerEmail', email.value)
    } else {
      setValue('financialOfficerName', '')
      setValue('financialOfficerEmail', '')
    }
  }

  const billingDataFieldsWithItems = billingDataFields.map(field => {
    return field.id === 'state'
      ? {
          ...field,
          items: {
            defaultElements: {
              elements: states,
            },
          },
        }
      : field
  })

  async function onZipBlur(element) {
    const [
      {
        target: { value },
      },
    ] = element
    const zip = value

    const { data } = await getAddressFromZipCode(country, zip)

    if (data) {
      setValue('city', data.city ? data.city.name : '')
      setValue('state', data.city ? data.city.state : '')
    }
  }

  const billingDataActions = {
    'checkout-billing-data-zip': {
      onBlur: onZipBlur,
    },
  }
  const billingDataFieldsWithActions = bindActionsOnFields(
    billingDataFieldsWithItems,
    billingDataActions
  )

  const financialManagerDataActions = {
    'financial-manager-data-same-as-contractor': {
      onChange: handleFinancialOfficerFields,
    },
  }
  const financialManagerDataFieldsWithActions = bindActionsOnFields(
    financialManagerDataFields,
    financialManagerDataActions
  )

  function handleBackToCommercialOffer() {
    onPrevious()
  }

  return (
    <Style.Form>
      <Style.Title>
        <FormattedMessage id="smb_acquisition_checkout.page.title" />
      </Style.Title>

      <Style.Fieldset>
        <Style.FormTitle>
          <enum>1.</enum>
          <FormattedMessage id="smb_acquisition_checkout.page.contractor_section.title" />
        </Style.FormTitle>
        <Style.Subtitle>
          <FormattedMessage id="smb_acquisition_checkout.page.contractor_section.description" />
        </Style.Subtitle>

        <DynamicForm
          fields={contractorDataFields}
          register={register}
          errors={errors}
          control={control}
        />
      </Style.Fieldset>

      <Style.Fieldset>
        <Style.FormTitle>
          <enum>2.</enum>
          <FormattedMessage id="smb_acquisition_checkout.page.company_section.title" />
        </Style.FormTitle>

        <DynamicForm
          fields={billingDataFieldsWithActions}
          register={register}
          errors={errors}
          control={control}
        />
      </Style.Fieldset>

      <Style.Fieldset>
        <Style.FormTitle>
          <enum>3.</enum>
          <FormattedMessage id="smb_acquisition_checkout.page.financial_manager_section.title" />
        </Style.FormTitle>

        <Style.Subtitle>
          <FormattedMessage id="smb_acquisition_checkout.page.financial_manager_section.description" />
        </Style.Subtitle>

        <DynamicForm
          fields={financialManagerDataFieldsWithActions}
          register={register}
          errors={errors}
          control={control}
        />
      </Style.Fieldset>

      <Style.Divider />

      <Style.Fieldset>
        <DynamicForm
          fields={checkoutWarningFields}
          register={register}
          errors={errors}
          control={control}
        />
      </Style.Fieldset>

      <Style.ButtonWrapper>
        <LoadingButton loading={isLoading} onClick={handleSubmit(onSubmit)}>
          <FormattedMessage id="smb_acquisition_checkout.page.call_to_action.advance" />
        </LoadingButton>

        <Button.Outline outlined onClick={handleBackToCommercialOffer}>
          <FormattedMessage id="smb_acquisition_checkout.page.call_to_action.return" />
        </Button.Outline>
      </Style.ButtonWrapper>
    </Style.Form>
  )
}

Form.propTypes = {
  onSubmit: func.isRequired,
  onPrevious: func.isRequired,
}
